/* ──────────────────────────────────────────────────────────────
   src/context/SocketContext.tsx
   Socket.IO compartido para toda la app - evita conexiones duplicadas
   ────────────────────────────────────────────────────────────── */

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./AuthContext";

const WS_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

interface SocketContextValue {
  socket: Socket | null;
  connected: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connected: false,
});

export function SocketProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      // Desconectar si no hay usuario
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    // Si ya hay socket con el mismo userId, no recrear
    const currentAuth = socketRef.current?.auth;
    if (currentAuth && typeof currentAuth === 'object' && 'userId' in currentAuth && currentAuth.userId === user.id) {
      return;
    }

    // Desconectar socket anterior si existe (cambio de usuario)
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    // Crear nuevo socket
    const socket = io(`${WS_BASE}/notifications`, {
      auth: { userId: user.id },
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user?.id]);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}

